import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, ExternalLink, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || "sso";
const CENTRALE_API_URL = process.env.REACT_APP_CENTRALE_API_URL || 'https://app.prysme.eu';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // États pour le formulaire local
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnectViaPrysma = () => {
    window.location.href = CENTRALE_API_URL;
  };

  const handleLocalLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(username, password);
      if (result.success) {
        navigate('/dashboard');
      } else {
        setError(result.error || 'Erreur de connexion');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  // MODE LOCAL : Formulaire classique (Preview/Dev)
  if (AUTH_MODE === "local") {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0a1628]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_47d6ac28-fd53-40fc-b71a-18fc6ec488aa/artifacts/50wfrc3g_AVATAR.png" 
              alt="Prysma Logo" 
              className="w-20 h-20 mx-auto mb-6 rounded-full"
            />
            <h1 className="text-3xl font-bold text-white mb-2">
              Annuaire Interne
            </h1>
            <p className="text-gray-400 text-sm">
              Connectez-vous pour accéder à l'annuaire
            </p>
          </div>

          {/* Formulaire */}
          <div className="bg-[#111d32] border border-[#1e3a5f] rounded-xl p-6">
            <form onSubmit={handleLocalLogin} className="space-y-4">
              {/* Identifiant */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Identifiant
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="prenom.nom"
                  className="w-full px-4 py-3 bg-[#0a1628] border border-[#1e3a5f] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors"
                  required
                />
              </div>

              {/* Mot de passe */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 bg-[#0a1628] border border-[#1e3a5f] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 transition-colors pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Erreur */}
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm text-center">{error}</p>
                </div>
              )}

              {/* Bouton de connexion */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-500/50 text-white font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </button>
            </form>
          </div>

          {/* Badge mode dev */}
          <div className="mt-6 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-full text-yellow-400 text-xs">
              <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
              Mode Développement
            </span>
          </div>
        </div>
      </div>
    );
  }

  // MODE SSO : Redirection vers Centrale Prysma (Production)
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0a1628]">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_47d6ac28-fd53-40fc-b71a-18fc6ec488aa/artifacts/50wfrc3g_AVATAR.png" 
            alt="Prysma Logo" 
            className="w-20 h-20 mx-auto mb-6 rounded-full"
          />
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Annuaire Interne
          </h1>
          <p className="text-2xl text-white font-light mb-2">
            Bienvenue
          </p>
          <p className="text-gray-400 text-sm">
            Gestionnaire de contacts interne
          </p>
        </div>

        {/* Security Card */}
        <div className="bg-[#111d32] border border-[#1e3a5f] rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-cyan-500/10 rounded-full flex items-center justify-center">
                <Shield className="h-6 w-6 text-cyan-400" />
              </div>
            </div>
            <div>
              <h2 className="text-white font-semibold text-lg mb-3">
                Authentification sécurisée requise
              </h2>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                Pour garantir la sécurité de vos données, l'accès à <span className="text-white font-medium">l'Annuaire Interne</span> se fait exclusivement via <span className="text-cyan-400 font-medium">Centrale Application Prysma</span>.
              </p>
              
              {/* Features List */}
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                  <span>Connexion unique SSO (Single Sign-On)</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                  <span>Session sécurisée par onglet</span>
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle className="h-4 w-4 text-cyan-400 flex-shrink-0" />
                  <span>Double authentification disponible</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Connect Button */}
        <button
          onClick={handleConnectViaPrysma}
          className="w-full bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 mb-4"
        >
          Se connecter via Prysma
          <ExternalLink className="h-5 w-5" />
        </button>

        {/* Footer Text */}
        <p className="text-center text-gray-500 text-xs">
          Vous serez redirigé vers la Centrale Application Prysma pour vous authentifier
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
